'use client';

import { env } from 'next-runtime-env';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiClientInstance } from '@shared/api';

declare global {
  interface Window {
    Goftino: {
      setWidget: (config: { hasIcon: boolean; cssUrl: string }) => void;
      getUserId: () => string | null | undefined;
      open: () => void;
    };
  }
}

const useGoftinoChat = () => {
  const [widgetSrc, setWidgetSrc] = useState<string | null>(null);
  const isRegisteredRef = useRef(false);
  const hasInitialized = useRef(false);

  const baseUrl = env('NEXT_PUBLIC_GOFTINO_BASE_URL') ?? '';
  const widgetId = env('NEXT_PUBLIC_GOFTINO_WIDGET_ID') ?? '';

  useEffect(() => {
    if (!baseUrl || !widgetId) return;

    const o = localStorage.getItem(`goftino_${widgetId}`);
    const url = `${baseUrl}widget/${widgetId}${o ? `?o=${o}` : ''}`;
    setWidgetSrc(url);
  }, [baseUrl, widgetId]);

  useEffect(() => {
    const handleReady = () => {
      if (hasInitialized.current) return;

      const goftino = window.Goftino;
      if (goftino?.setWidget) {
        goftino.setWidget({
          hasIcon: false,
          cssUrl: 'https://saraa.asax.ir/assets/css/GoftinoCustomWidgetStyleV1.3.css',
        });
        hasInitialized.current = true;
      }
    };

    window.addEventListener('goftino_ready', handleReady);
    return () => {
      window.removeEventListener('goftino_ready', handleReady);
    };
  }, []);

  const openChat = useCallback(async () => {
    const goftino = window.Goftino;

    if (!goftino?.getUserId) return;

    const userId = goftino.getUserId();

    if (!userId || isRegisteredRef.current) {
      goftino.open();
      return;
    }

    try {
      await ApiClientInstance.post<string, void>(`v2/support/chat`, userId);
      isRegisteredRef.current = true;
    } catch (error: any) {
      if (error) console.error('registration failed!', { error });
    }

    goftino.open();
  }, []);

  const logoutChat = useCallback(() => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('goftino')) {
        localStorage.removeItem(key);
      }
    });
  }, []);

  return { widgetSrc, openChat, logoutChat };
};

export { useGoftinoChat };
